/**
 * Shared UI building blocks for RumahKira pages
 */

import React from 'react';
import {
  Box,
  ButtonBase,
  Card,
  Container,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';

/**
 * Page wrapper with consistent width and padding.
 * Bottom padding leaves room for the mobile tab bar and sticky action bars.
 */
export const Page = ({ children, maxWidth = 'md', sx }) => (
  <Container maxWidth={maxWidth} sx={{ pt: { xs: 2, md: 4 }, pb: { xs: 12, md: 6 }, ...sx }}>
    {children}
  </Container>
);

/**
 * Page title row with optional back button and actions
 */
export const PageHeader = ({ title, subtitle, onBack, backLabel = 'Back', actions, overline }) => (
  <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
    {onBack && (
      <ButtonBase
        onClick={onBack}
        sx={{
          mb: 1.5,
          ml: -0.5,
          px: 0.5,
          py: 0.25,
          borderRadius: '6px',
          color: 'text.secondary',
          typography: 'body2',
          fontWeight: 600,
          gap: 0.5,
          '&:hover': { color: 'text.primary' },
        }}
      >
        <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
        {backLabel}
      </ButtonBase>
    )}
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
      <Box sx={{ minWidth: 0 }}>
        {overline && (
          <Typography variant="overline" color="primary" component="div">
            {overline}
          </Typography>
        )}
        <Typography variant="h4" component="h1">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && <Box sx={{ flexShrink: 0, display: 'flex', gap: 1 }}>{actions}</Box>}
    </Stack>
  </Box>
);

/**
 * Card with an optional header (title, subtitle, action)
 */
export const SectionCard = ({ title, subtitle, action, children, sx, contentSx, icon }) => (
  <Card sx={{ mb: 2, ...sx }}>
    {(title || action) && (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ px: { xs: 2, sm: 2.5 }, pt: 2, pb: subtitle ? 0.5 : 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
          {icon && <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>}
          <Typography variant="h6" component="h2" noWrap>
            {title}
          </Typography>
        </Stack>
        {action}
      </Stack>
    )}
    {subtitle && (
      <Typography variant="body2" color="text.secondary" sx={{ px: { xs: 2, sm: 2.5 }, pb: 1 }}>
        {subtitle}
      </Typography>
    )}
    <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: 2.5, pt: title ? 1 : 2.5, ...contentSx }}>{children}</Box>
  </Card>
);

/**
 * Field label used above custom inputs
 */
export const FieldLabel = ({ children, hint }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="subtitle2" component="div">
      {children}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    )}
  </Box>
);

/**
 * Selectable card (radio-style). Keyboard accessible.
 */
export const OptionCard = ({ selected, onClick, icon, media, title, description, meta, badge, sx }) => (
  <ButtonBase
    onClick={onClick}
    role="radio"
    aria-checked={!!selected}
    sx={(theme) => ({
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      textAlign: 'left',
      width: '100%',
      height: '100%',
      p: 1.75,
      borderRadius: '14px',
      border: '1.5px solid',
      borderColor: selected ? 'primary.main' : 'divider',
      bgcolor: selected ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.05) : 'background.paper',
      transition: 'border-color 120ms, background-color 120ms',
      '&:hover': { borderColor: selected ? 'primary.main' : 'text.disabled' },
      '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
      ...sx,
    })}
  >
    {selected && (
      <CheckCircleRoundedIcon color="primary" sx={{ position: 'absolute', top: 10, right: 10, fontSize: 20 }} />
    )}
    {badge && (
      <Box
        sx={{
          position: 'absolute',
          top: -10,
          left: 12,
          px: 1,
          py: 0.125,
          borderRadius: '6px',
          bgcolor: 'secondary.main',
          color: 'secondary.contrastText',
          typography: 'caption',
          fontWeight: 700,
        }}
      >
        {badge}
      </Box>
    )}
    {media && <Box sx={{ mb: 1.25 }}>{media}</Box>}
    {icon && <Box sx={{ color: selected ? 'primary.main' : 'text.secondary', mb: 1, display: 'flex' }}>{icon}</Box>}
    <Typography variant="subtitle1" sx={{ pr: 3, lineHeight: 1.3 }}>
      {title}
    </Typography>
    {description && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
        {description}
      </Typography>
    )}
    {meta && (
      <Typography variant="caption" color={selected ? 'primary' : 'text.secondary'} sx={{ mt: 0.75, fontWeight: 600 }}>
        {meta}
      </Typography>
    )}
  </ButtonBase>
);

/**
 * Row with label and − / + buttons
 */
export const Counter = ({ label, helper, value, onChange, min = 0, max = 20, icon }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    spacing={2}
    sx={{ py: 1.25, borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 'none' } }}
  >
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
      {icon && <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
        {helper && (
          <Typography variant="caption" color="text.secondary">
            {helper}
          </Typography>
        )}
      </Box>
    </Stack>
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <IconButton
        size="small"
        aria-label={`${label} −`}
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        sx={{ border: '1px solid', borderColor: 'divider' }}
      >
        <RemoveRoundedIcon fontSize="small" />
      </IconButton>
      <Typography className="tabular" sx={{ minWidth: 28, textAlign: 'center', fontWeight: 700 }}>
        {value}
      </Typography>
      <IconButton
        size="small"
        aria-label={`${label} +`}
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        sx={{ border: '1px solid', borderColor: 'divider' }}
      >
        <AddRoundedIcon fontSize="small" />
      </IconButton>
    </Stack>
  </Stack>
);

/**
 * Numeric text field with a unit suffix or unit selector.
 * `units` = [{ id, label }] shows a selector; otherwise `unit` is shown as plain text.
 */
export const NumberField = ({
  label,
  value,
  onChange,
  unit,
  units,
  unitValue,
  onUnitChange,
  helperText,
  placeholder,
  integer = false,
  ...props
}) => (
  <TextField
    fullWidth
    label={label}
    value={value ?? ''}
    placeholder={placeholder}
    helperText={helperText}
    onChange={(e) => {
      const raw = e.target.value.replace(',', '.');
      const cleaned = integer ? raw.replace(/[^0-9]/g, '') : raw.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
      onChange(cleaned);
    }}
    inputProps={{ inputMode: integer ? 'numeric' : 'decimal', className: 'tabular' }}
    InputProps={{
      endAdornment:
        units && units.length > 1 ? (
          <InputAdornment position="end" sx={{ mr: -1.5 }}>
            <Select
              variant="standard"
              disableUnderline
              value={unitValue}
              onChange={(e) => onUnitChange(e.target.value)}
              sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.secondary', '& .MuiSelect-select': { py: 0.5, pr: 3.5 } }}
              inputProps={{ 'aria-label': `${label} unit` }}
            >
              {units.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.label}
                </MenuItem>
              ))}
            </Select>
          </InputAdornment>
        ) : unit ? (
          <InputAdornment position="end">
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {unit}
            </Typography>
          </InputAdornment>
        ) : undefined,
    }}
    {...props}
  />
);

/**
 * Coloured rounded icon tile
 */
export const IconTile = ({ icon, color = 'primary', size = 44, sx }) => (
  <Box
    sx={(theme) => {
      const c = theme.palette[color]?.main || color;
      return {
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: `${Math.round(size * 0.28)}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: c,
        bgcolor: alpha(c, theme.palette.mode === 'dark' ? 0.18 : 0.1),
        '& svg': { fontSize: size * 0.5 },
        ...sx,
      };
    }}
  >
    {icon}
  </Box>
);

/**
 * Empty state with icon, text and an action
 */
export const EmptyState = ({ icon, title, description, action }) => (
  <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
    {icon && <IconTile icon={icon} size={56} sx={{ mx: 'auto', mb: 2 }} />}
    <Typography variant="h6">{title}</Typography>
    {description && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 380, mx: 'auto' }}>
        {description}
      </Typography>
    )}
    {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
  </Box>
);

/**
 * Action bar that sticks to the bottom of the screen on mobile (above the tab bar)
 * and sits inline at the end of the content on desktop.
 */
export const StickyActions = ({ children }) => (
  <>
  <Box sx={{ display: { xs: 'block', md: 'none' }, height: 72 }} aria-hidden="true" />
  <Box
    sx={{
      position: { xs: 'fixed', md: 'static' },
      left: 0,
      right: 0,
      bottom: { xs: 'calc(64px + env(safe-area-inset-bottom))', md: 'auto' },
      zIndex: (theme) => theme.zIndex.appBar - 1,
      px: { xs: 2, md: 0 },
      py: { xs: 1.5, md: 0 },
      mt: { md: 1 },
      bgcolor: { xs: 'background.paper', md: 'transparent' },
      borderTop: { xs: '1px solid', md: 'none' },
      borderColor: 'divider',
    }}
  >
    <Stack direction="row" spacing={1.5} sx={{ maxWidth: 900, mx: 'auto' }}>
      {children}
    </Stack>
  </Box>
  </>
);
